import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { type NegotiationRow, api } from "../lib/api";
import { DealSignPanel } from "./Deals";

type ListResp = { items: NegotiationRow[]; total: number };
type MeResp = {
	user: { id: string; email: string | null };
	org: { id: string; kind: "brand" | "affiliate" } | null;
};

type Offer = {
	id: string;
	payoutType: "cpa" | "cpl" | "revshare" | "hybrid";
	payoutAmount: number | null;
	payoutPercent: number | null;
	payoutCurrency: string;
};

export function Negotiations() {
	const me = useQuery<MeResp>({ queryKey: ["me"], queryFn: () => api.get("/me") });
	const { data, isLoading } = useQuery<ListResp>({
		queryKey: ["negotiations"],
		queryFn: () => api.get("/negotiations"),
	});

	if (isLoading) return <div className="p-6">Loading…</div>;
	const items = data?.items ?? [];
	const orgKind = me.data?.org?.kind;

	return (
		<div className="p-6 space-y-4">
			<h1 className="text-xl font-semibold">Negotiations</h1>
			{items.length === 0 && (
				<div className="text-sm text-neutral-500">
					No negotiations yet. They start when an affiliate POSTs /negotiations against an active
					offer.
				</div>
			)}
			{items.map((n) => (
				<NegotiationCard key={n.id} neg={n} side={orgKind} />
			))}
		</div>
	);
}

function NegotiationCard(props: { neg: NegotiationRow; side: "brand" | "affiliate" | undefined }) {
	const { neg } = props;
	const [showSign, setShowSign] = useState(false);
	const offer = useQuery<Offer>({
		queryKey: ["offer", neg.offerId],
		queryFn: () => api.get(`/offers/${neg.offerId}`),
		enabled: showSign,
	});

	const canSign = neg.state === "open" || neg.state === "manager_approval";

	return (
		<div className="rounded border border-neutral-200 bg-white p-3 shadow-sm">
			<div className="flex items-baseline justify-between">
				<div className="text-sm font-medium">
					Offer <span className="font-mono">{neg.offerId.slice(0, 8)}…</span> · {neg.state}
				</div>
				<div className="text-xs text-neutral-500">
					ttl {new Date(neg.ttlAt * 1000).toLocaleString()}
				</div>
			</div>
			<div className="mt-1 text-xs text-neutral-500">
				brand <span className="font-mono">{neg.brandOrgId.slice(0, 8)}…</span> · affiliate{" "}
				<span className="font-mono">{neg.affiliateOrgId.slice(0, 8)}…</span>
			</div>
			{canSign && props.side && (
				<div className="mt-3">
					{!showSign ? (
						<button
							type="button"
							onClick={() => setShowSign(true)}
							className="rounded bg-emerald-600 px-3 py-1 text-xs text-white hover:bg-emerald-700"
						>
							Sign deal
						</button>
					) : offer.data ? (
						<DealSignPanel
							negotiationId={neg.id}
							offerId={neg.offerId}
							brandOrgId={neg.brandOrgId}
							affiliateOrgId={neg.affiliateOrgId}
							payoutType={offer.data.payoutType}
							payoutAmount={offer.data.payoutAmount}
							payoutPercent={offer.data.payoutPercent}
							payoutCurrency={offer.data.payoutCurrency}
							side={props.side}
						/>
					) : (
						<div className="text-xs text-neutral-500">Loading offer terms…</div>
					)}
				</div>
			)}
		</div>
	);
}
